<template>
  <v-app-bar app color="white" elevation="1" height="64">
    <!-- Menu Button -->
    <v-app-bar-nav-icon @click="toggleDrawer" />

    <!-- App Title -->
    <v-toolbar-title class="text-h6 font-weight-bold text-primary">
      {{ company }}
    </v-toolbar-title>

    <v-spacer />

    <!-- System Information -->
    <div class="d-flex align-center ga-3 mx-4">
      <!-- Current Date & Time -->
      <v-chip variant="outlined" size="small" prepend-icon="mdi-calendar-clock">
        {{ currentDateTime }}
      </v-chip>

      <!-- Online Status -->
      <v-chip
        :color="isOnline ? 'success' : 'error'"
        variant="flat"
        size="small"
        prepend-icon="mdi-circle"
      >
        {{ isOnline ? 'Online' : 'Offline' }}
      </v-chip>
    </div>

    <!-- User Menu -->
    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn icon v-bind="props" class="mx-2">
          <v-avatar size="32" color="primary">
            <v-icon v-if="!userAvatar">mdi-account</v-icon>
            <v-img v-else :src="userAvatar" alt="User Avatar" />
          </v-avatar>
        </v-btn>
      </template>

      <v-list min-width="250">
        <!-- User Info -->
        <v-list-item>
          <template #prepend>
            <v-avatar size="40" color="primary">
              <v-icon v-if="!userAvatar">mdi-account</v-icon>
              <v-img v-else :src="userAvatar" alt="User Avatar" />
            </v-avatar>
          </template>
          <v-list-item-title class="font-weight-medium">
            {{ authStore.userFullName }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ authStore.user?.email }}
          </v-list-item-subtitle>
        </v-list-item>

        <v-divider />

        <v-list-item @click="openChangePassword">
          <template #prepend>
            <v-icon>mdi-lock</v-icon>
          </template>
          <v-list-item-title>Change Password</v-list-item-title>
        </v-list-item>

        <v-divider />

        <v-list-item @click="handleLogout" class="text-error">
          <template #prepend>
            <v-icon color="error">mdi-logout</v-icon>
          </template>
          <v-list-item-title>Logout</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>

  <!-- Change Password Dialog -->
  <v-dialog v-model="showChangePassword" max-width="400">
    <v-card>
      <v-card-title>Change Password</v-card-title>
      <v-card-text>
        <v-form v-model="isPasswordFormValid" @submit.prevent="changePassword">
          <v-text-field
            v-model="passwordForm.currentPassword"
            :rules="[(v: any) => !!v || 'Current password is required']"
            type="password"
            label="Current Password"
            variant="outlined"
            class="mb-4"
          />
          <v-text-field
            v-model="passwordForm.newPassword"
            :rules="passwordRules"
            type="password"
            label="New Password"
            variant="outlined"
            class="mb-4"
          />
          <v-text-field
            v-model="passwordForm.confirmPassword"
            :rules="confirmPasswordRules"
            type="password"
            label="Confirm New Password"
            variant="outlined"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="closeChangePassword">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="changePassword"
          :loading="authStore.loading"
          :disabled="!isPasswordFormValid"
        >
          Change Password
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/useAuthStore'

// Props and emits
const props = defineProps<{
  drawer: boolean
  search: string
}>()

const emit = defineEmits<{
  'update:drawer': [value: boolean]
  'update:search': [value: string]
  logout: []
}>()

// Dependencies
const authStore = useAuthStore()
const showSnackbar = inject<(message: string, color?: string) => void>('showSnackbar')

// Reactive state
const userAvatar = ref<string>() // You can set this from user profile
const company = ref(authStore.terminalSite?.siteName || '')
const showChangePassword = ref(false)
const isPasswordFormValid = ref(false)
const currentDateTime = ref('')
const isOnline = ref(navigator.onLine)

// Password form
const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

// Validation rules
const passwordRules = [
  (v: string) => !!v || 'Password is required',
  (v: string) => v.length >= 8 || 'Password must be at least 8 characters',
  (v: string) => /(?=.*[a-z])/.test(v) || 'Password must contain lowercase letter',
  (v: string) => /(?=.*[A-Z])/.test(v) || 'Password must contain uppercase letter',
  (v: string) => /(?=.*\d)/.test(v) || 'Password must contain number',
  (v: string) => /(?=.*[@$!%*?&])/.test(v) || 'Password must contain special character',
]

const confirmPasswordRules = [
  (v: string) => !!v || 'Please confirm your password',
  (v: string) => v === passwordForm.value.newPassword || 'Passwords do not match',
]

// Methods
const updateDateTime = () => {
  const now = new Date()
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  currentDateTime.value = now.toLocaleDateString('en-US', options)
}

const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine
}

const toggleDrawer = () => {
  emit('update:drawer', !props.drawer)
}

const handleLogout = () => {
  emit('logout')
}

const openChangePassword = () => {
  showChangePassword.value = true
  passwordForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }
}

const closeChangePassword = () => {
  showChangePassword.value = false
  passwordForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }
}

const changePassword = async () => {
  if (!isPasswordFormValid.value) return

  try {
    const success = await authStore.changePassword({
      currentPassword: passwordForm.value.currentPassword,
      newPassword: passwordForm.value.newPassword,
    })

    if (success) {
      showSnackbar?.('Password changed successfully', 'success')
      closeChangePassword()
    } else {
      showSnackbar?.(authStore.error || 'Failed to change password', 'error')
    }
  } catch (error) {
    console.error('Password change error:', error)
    showSnackbar?.('An error occurred while changing password', 'error')
  }
}

// Lifecycle
let dateTimeInterval: ReturnType<typeof setInterval>

onMounted(() => {
  updateDateTime()
  dateTimeInterval = setInterval(updateDateTime, 60000) // Update every minute

  // Listen for online/offline events
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)
})

onUnmounted(() => {
  if (dateTimeInterval) {
    clearInterval(dateTimeInterval)
  }
  window.removeEventListener('online', updateOnlineStatus)
  window.removeEventListener('offline', updateOnlineStatus)
})
</script>

<style scoped>
.search-field {
  border-radius: 24px;
}

.v-toolbar-title {
  margin-left: 16px;
}

.v-badge {
  margin-right: 8px;
}
</style>
